import React from "react";
import PropTypes from 'prop-types';
import cn from 'classnames';

// import commoncss from '~/themes/common.css';

import Tooltip from "~/ui/Tooltip/Tooltip";

import { connect } from '~/redux/connect';
import { presentColumns, deriveNewListOrder } from "~/statePresenter";
import { getCalendar, getModelTooltip, getPresentColumnsData, getTooltipsActive } from "~/stateGetter";
import css from './tableSettings.css';
import { settingsActionCreator } from "../../actions/settingsActions";
import debounce from 'lodash/debounce';
import { Box, IconMenu, MenuDivider, MenuGroup, MenuItem, Menu } from "~/native-ui"
import { HighLightText, SpinnerInput } from "../../native-ui";
import { Sortable } from "~/native-ui/sortable/Sortable";

class TableSettings extends React.Component {
	static propTypes = {
        tooltipsActive: PropTypes.bool.isRequired,
        tableSettingsTooltip: PropTypes.string.isRequired,
        changeColumnVisibility: PropTypes.func.isRequired,
        columns: PropTypes.array.isRequired,
        isCalendarEnabled: PropTypes.bool.isRequired,
        mode: PropTypes.oneOf(["menuitem", "button"]),
	};

    notUniqueTextValue = String(new Date().getTime());
    shouldSetFocus = false;

    state = {
        searchTerm: "",
    }

    searchInputRef = React.createRef(null);

    
    resetColumns = () => {
        this.props.resetColumns();
    }
    
    onReorder = (oldIndex, newIndex) => {
        this.props.onColumnReorder({
            ...this.props.columnOrder,
            ...this.props.deriveNewListOrder(this.props.columns, oldIndex, newIndex)
        })
    }

    getCaption(column) {
        let { hidden, name, title } = column;
        return ( title )
    }

    handleOpen = () => {
        this.shouldSetFocus = true;
    }


    renderResetButton () {
        return (
            <Box
                bottom="0"
                position="sticky"
                backgroundColor="#fff"
            >
                <MenuDivider />
                <MenuItem
                    key={"reset-columns"}
                    textValue={this.notUniqueTextValue}
                    caption={this.props.trans("reset_columns")}
                    onClick={this.resetColumns}
                    orderable={false}
                />
            </Box>
        )
    }

    setSearchTerm = (searchTerm, event) => {
        event?.preventDefault();
        event?.stopPropagation();
        this.setState({ searchTerm })
    }

    setSearchRef = (ref) => {
        this.searchInputRef.current = ref;
        if (this.shouldSetFocus) {
            this.shouldSetFocus = false;
            setTimeout(() => { ref?.focus?.(); })
        }
    }
    

    renderSearch () {
        return (
            <MenuItem
                pt="0"
                bottom="0"
                position="sticky"
                backgroundColor="#fff"
                orderable={false}
                textValue={this.notUniqueTextValue}
            >
                <SpinnerInput 
                    placeholder={this.props.trans('search')}
                    value={this.state.searchTerm}
                    // className={css.search}
                    onChange={this.setSearchTerm}
                    ref={this.setSearchRef}
                />
            </MenuItem>
        )
    }

    menuProps = {
        closeOnSelect: false,
        canReorder: true,
        onReorder: this.onReorder,
        orderable: true,
        shouldOverlapWithTrigger: true,
        onKeyDown: this.setSearchTerm,
    }

    renderTrigger = (triggerProps) => {
        return <MenuItem
            icon="table-headers-eye-off"
            {...triggerProps}
            onClick={triggerProps.onPress}
            iconFamily="material-community"
            iconColor="primary.500"
            caption={this.props.trans('columns_configuration.hideColumns')}
        />
    }

    renderAsMenuItem = (props) => {
        return (
           <Menu
                {...this.menuProps}
                trigger={this.renderTrigger} 
                onOpen={this.handleOpen}
            >
                {this.renderMenu()} 
            </Menu>
        )
    }

    renderColumnItem = (renderedColumns, column, i) => {
        const regex = this.state.searchTerm ? new RegExp(this.state.searchTerm, "ig") : null
        let { hidden, name, title } = column;

        if(title.length < 1) {
            return renderedColumns;
        }

        if(regex && !regex.test(title)) {
            return renderedColumns;
        }

        const onClick = debounce(() => { this.props.changeColumnVisibility(name) }, { leading: true })
        renderedColumns.push(<MenuItem
            key={name}
            checkable
            orderable
            index={i}
            textValue={this.notUniqueTextValue}
            value={column.name}
            caption={<HighLightText key={name+"caption"} value={this.getCaption(column)} searchTerm={this.state.searchTerm} />}
            className={cn({["uncheckedMenuItem"]: hidden})}
            onClick={onClick}
        />);

        return renderedColumns;
    };
d
    getVisibleColumns = (columns = this.props.columns) => {
        return columns.reduce((visibleColumns, { hidden, name }) => hidden ? visibleColumns : visibleColumns.concat(name), []);
    }

    renderMenu = () => {
        return <>
            {this.renderSearch()}
            <MenuGroup value={ this.getVisibleColumns() } checkbox>
                {this.props.columns.reduce(this.renderColumnItem, [])}
            </MenuGroup>
            {this.renderResetButton()}
        </>
    }

    deriveLabel = () => {
        const hiddenColumnsCount = this.props.columns.length - this.getVisibleColumns().length
        return hiddenColumnsCount ? this.props.trans('columns_configuration.hiddenColumnsCount', { count: hiddenColumnsCount }) : this.props.trans('columns_configuration.hideColumns');
    }

    renderAsButton() {
        let { columns, isCalendarEnabled } = this.props;

        if (isCalendarEnabled) {
            return null;
        }

        const hiddenColumnsCount = columns.length - this.getVisibleColumns().length


        const buttonProps = {
            ml: "2",
            primary: true,
            iconSize: "sm",
            position: "relative",
            // badge: undefined hiddenColumnsCount,
            variant: hiddenColumnsCount ? "ghost_faded" : undefined,
            ...this.props.buttonProps
        }
        
        const label = this.deriveLabel();

        return (
            <React.Fragment>
                <IconMenu icon="table-headers-eye-off" tooltip={label} iconFamily="material-community" buttonProps={buttonProps} menuProps={ this.menuProps } onOpen={this.handleOpen}>
                    {this.renderMenu()}
                </IconMenu>
                <Tooltip className={css.tableSettingsTooltip} tooltip={this.props.tableSettingsTooltip} />
            </React.Fragment>
        )
    };


    render() {
        return this.props.mode === 'menuitem' ? this.renderAsMenuItem() : this.renderAsButton();
    }
}

export default connect(
    (state, {modelId}) => ({
        tooltipsActive: getTooltipsActive(state),
        tableSettingsTooltip: getModelTooltip(state, modelId, 'table_settings'),
        isCalendarEnabled: getCalendar(state, modelId).isCalendarEnabled,
        ...getPresentColumnsData(state, modelId),
    }),
    (dispatch, {modelId}) => {
        let settingActions = settingsActionCreator(modelId);

        return {
            changeColumnVisibility: (name) => dispatch(settingActions.toggleColumnVisibility(name)),
            onColumnReorder: (columnOrder) => dispatch(settingActions.changeColumnOrder(columnOrder)),
            resetColumns: () => dispatch(settingActions.resetColumns())
        }
    },
    (props, dispatchProps, ownProps) => ({
        ...ownProps,
        ...dispatchProps,
        ...props,
        deriveNewListOrder,
        columns: presentColumns(props),
    })
)(TableSettings)